/**
 * Secure AI Proxy Client
 * 
 * This client proxies AI API calls through a Cloudflare Worker
 * to keep API keys secure (never exposed to the browser).
 */

import { apiEndpoints } from '../../config/environment';
import { fetchWithTimeout } from './timedFetch';

export type AIProviderType = 'gemini' | 'openai' | 'anthropic';

interface ProxyRequest {
  provider: AIProviderType;
  endpoint: string;
  body: any;
  method?: string;
}

export interface ProxyOptions {
  proxyUrl?: string;
  headers?: HeadersInit;
  signal?: AbortSignal;
  timeoutMs?: number;
}

/**
 * Call AI API through secure proxy
 * Supports: OpenAI, Gemini, Anthropic (Claude)
 */
export async function callAIProxy(
  provider: AIProviderType,
  endpoint: string,
  body: any,
  methodOrOptions: string | ProxyOptions = 'POST',
  options: ProxyOptions = {}
): Promise<any> {
  const method = typeof methodOrOptions === 'string' ? methodOrOptions : 'POST';
  const proxyOptions = typeof methodOrOptions === 'string' ? options : methodOrOptions;
  const workerUrl = proxyOptions.proxyUrl ?? apiEndpoints.aiWorkerUrl;

  if (!workerUrl) {
    throw new Error('AI proxy not configured. Set VITE_AI_WORKER_URL or provide proxyUrl.');
  }

  try {
    const headers = new Headers(proxyOptions.headers);
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    const response = await fetchWithTimeout(workerUrl, {
      signal: proxyOptions.signal,
      method: 'POST',
      headers,
      credentials: 'include', // Include Cloudflare Access cookies
      body: JSON.stringify({
        provider,
        endpoint,
        body,
        method,
      } as ProxyRequest),
    }, proxyOptions.timeoutMs);

    if (!response.ok) {
      const errorText = await response.text();
      let errorData: any;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText || `HTTP ${response.status}` };
      }
      const errorMessage = errorData.error || errorData.message || `HTTP ${response.status}`;
      console.error('AI proxy error:', errorMessage, errorData);
      // Include more context in the error
      throw new Error(`AI Proxy Error (${response.status}): ${errorMessage}`);
    }

    const responseData = await response.json();
    
    // Check if response contains an error
    if (responseData.error) {
      console.error('AI proxy returned error:', responseData.error);
      throw new Error(responseData.error);
    }
    
    return responseData;
  } catch (error) {
    console.warn('AI proxy call failed:', error);
    throw error;
  }
}

/**
 * Check if AI proxy is available
 */
export function isAIProxyAvailable(): boolean {
  return Boolean(apiEndpoints.aiWorkerUrl);
}
